import * as React from "react";
import styles from "./Glossary.module.scss";
import {useEffect, useRef} from "react";
import {Row, Col, Skeleton} from "antd";
import classnames from "classnames";
import {FocusedTerm} from "./Glossary";
import {DatasetMenu, DatasetMenuProps} from "./DatasetMenu";
import {DatasetTags} from "./DatasetTag";
import { GlossaryData, TermsGroup} from "./glossarySlice";
import MarkdownEditor from "@uiw/react-markdown-editor";

export interface GlossaryGroupItem {
    title: string
    description: string
    datasets: {id: number, title: string}[] | undefined,
}

const toDataTestId = (title: string) => {
    return 'glossary-item-' + title.replace(/[^A-Za-z0-9\s]/gi, '')
        .replace(/\s/g, '-')
        .toLowerCase()
}

const isFocusedItem = (focusedTerm: FocusedTerm, item: GlossaryGroupItem) => {
    return focusedTerm.title.toLowerCase() === item.title.toLowerCase()
}

const isSearchedItem = (focusedTerm: FocusedTerm, item: GlossaryGroupItem) => {
    return focusedTerm.searchTitle.toLowerCase() === item.title.toLowerCase()
}

const shouldPopulateRef = (index: number, focusedTerm: FocusedTerm, item: GlossaryGroupItem): boolean => {
    if (focusedTerm.title === '') {
        return index === 0
    }

    return isFocusedItem(focusedTerm, item)
}

const refPopulated = (
    ref: React.RefObject<HTMLParagraphElement | null>,
): ref is React.RefObject<HTMLParagraphElement> & {current: HTMLParagraphElement} => {
    return ref !== null && ref.current !== null
}

const GlossaryGroup = ({containerRef, termsGroup, focusedTerm, groupIndex, setActiveKey}: {
    containerRef: React.RefObject<HTMLDivElement | null>,
    termsGroup: TermsGroup,
    focusedTerm: FocusedTerm,
    groupIndex: number,
    setActiveKey: (key: string) => void
}) => {
    const titleRef = useRef<HTMLParagraphElement>(null)
    const lastItemRef = useRef<HTMLParagraphElement>(null)
    const groupStartRef = useRef<HTMLDivElement>(null)
    const groupEndRef = useRef<HTMLDivElement>(null)
    const lastScrollPosition = useRef(0)
    const eventListenerAdded = useRef(false)

    useEffect(() => {
        let eventListener: () => void
        let current = containerRef.current
        if (refPopulated(containerRef) && !eventListenerAdded.current) {
            eventListenerAdded.current = true
            eventListener = function() {
                if (refPopulated(groupStartRef) && refPopulated(groupEndRef) && refPopulated(lastItemRef)) {
                    const scrollPosition = containerRef.current.scrollTop
                    const isScrollDown = scrollPosition > lastScrollPosition.current

                    const firstItemTop = groupStartRef.current.offsetTop - Math.floor(groupEndRef.current.offsetHeight / 3)
                    const lastItemBottom = isScrollDown ? groupEndRef.current.offsetTop + Math.floor(groupEndRef.current.offsetHeight * 2/3) : lastItemRef.current.offsetTop

                    const topOfGroup = Math.max(firstItemTop - containerRef.current.offsetTop, 0)
                    const bottomOfGroup = Math.max(lastItemBottom - containerRef.current.offsetTop, 0)
                    if (scrollPosition >= topOfGroup && scrollPosition <= bottomOfGroup) {
                        setActiveKey(groupIndex.toString())
                    }
                    lastScrollPosition.current = containerRef.current.scrollTop
                }
            }

            containerRef.current.addEventListener('scroll', eventListener)

            return () => {
                if (current !== undefined && current !== null && eventListener !== undefined) {
                    current.removeEventListener('scroll', eventListener)
                }
            }
        }
    }, [groupIndex, setActiveKey, containerRef, groupStartRef, groupEndRef, lastScrollPosition])

    useEffect(() => {
        if (refPopulated(containerRef) &&  refPopulated(titleRef)) {
            if (focusedTerm.title !== '') {
                containerRef.current.scrollTop = titleRef.current.offsetTop
            }
        }
    }, [focusedTerm, containerRef])

    return <>
        <div ref={groupStartRef}>
            {termsGroup.terms.map((term, index) => {
                return <div
                    data-testid={toDataTestId(term.title)}
                    className={classnames(styles.glossaryItem)}
                    key={term.id}
                    ref={index === termsGroup.terms.length - 1 ? lastItemRef : undefined
                    }>
                    <p
                        ref={shouldPopulateRef(index, focusedTerm, term) ?
                            titleRef : undefined
                        }
                        data-testid={shouldPopulateRef(index, focusedTerm, term) ?
                            'glossary-item-title-focused' : undefined
                        }
                        className={classnames(
                            styles.glossaryItemTitle,
                            {[styles.glossaryItemTitleFocused]: isSearchedItem(focusedTerm, term)}
                        )}
                    >
                        {term.title}
                    </p>

                    <div className={classnames(styles.glossaryMarkDownView)}>
                        <MarkdownEditor.Markdown source={term.description}/>
                    </div>

                    <DatasetTags datasets={term.datasets}/>
                </div>
            })}
        </div>

    </>
}

export type GlossaryTabContentProps = {
    glossary: GlossaryData | undefined
    focusedTerm: FocusedTerm,
    setFocusedTerm: (term: FocusedTerm) => void,
    setActiveKey: (key: string) => void
    selectedDataset: DatasetMenuProps['selectedDataset']
    setSelectedDataset: DatasetMenuProps['setSelectedDataset']
}

export const GlossaryTabContent = ({
    glossary, focusedTerm, setFocusedTerm, setActiveKey, selectedDataset, setSelectedDataset
}: GlossaryTabContentProps) => {
    const containerRef = useRef<HTMLDivElement>(null)

    const onSetSelectedDataset = (datasetId: number | undefined) => {
        if (datasetId !== selectedDataset) {
            if (containerRef.current !== null) {
                containerRef.current.scrollTop = 0
            }
            setSelectedDataset(datasetId)
            setFocusedTerm({title: '', searchTitle: ''})
            setActiveKey("0")
        }
    }

    return <Row wrap={false} ref={containerRef} className={styles.glossaryTermsContainer}>
        <Col span={4} style={{position: 'sticky', top: '0'}}>
            <DatasetMenu selectedDataset={selectedDataset} setSelectedDataset={onSetSelectedDataset} />
        </Col>
        <Col span={20} data-testid="glossary-terms-container">
            {glossary === undefined ? <Skeleton active/> : glossary.map((termsGroup, index) => {
                return <GlossaryGroup
                    containerRef={containerRef}
                    key={termsGroup.key}
                    termsGroup={termsGroup}
                    focusedTerm={focusedTerm}
                    groupIndex={index}
                    setActiveKey={setActiveKey}
                />
            })}
            <div className={styles['last-item-space']}></div>
        </Col>
    </Row>
}